import { type NcbiCredentials, ncbiFetch } from "@fetch/genbank/fetch";

export type NcbiSequenceSummary = {
  accession: string;
  version: string;
  title: string;
  organism: string;
  length: number;
};

type EsummaryResult = {
  accessionversion: string;
  title: string;
  organism: string;
  slen: string;
};

type EsummaryResponse = {
  result: {
    uids: string[];
    [uid: string]: EsummaryResult | string[];
  };
};

export async function fetchEsummary(
  accessions: string[],
  credentials?: NcbiCredentials,
  signal?: AbortSignal,
): Promise<Map<string, NcbiSequenceSummary>> {
  const summaries = new Map<string, NcbiSequenceSummary>();

  if (accessions.length === 0) {
    return summaries;
  }

  const response = await ncbiFetch(
    "esummary.fcgi",
    {
      db: "nucleotide",
      id: accessions.join(","),
      retmode: "json",
    },
    credentials,
    signal,
  );

  if (!response.ok) {
    return summaries;
  }

  const data: EsummaryResponse = await response.json();

  if (!data.result?.uids) {
    return summaries;
  }

  for (const uid of data.result.uids) {
    const record = data.result[uid] as EsummaryResult;

    if (!record?.accessionversion) {
      continue;
    }

    const version = record.accessionversion;
    const accession = version.includes(".")
      ? version.slice(0, version.lastIndexOf("."))
      : version;

    const summary: NcbiSequenceSummary = {
      accession,
      version,
      title: record.title,
      organism: record.organism,
      length: Number.parseInt(record.slen, 10),
    };

    summaries.set(accession, summary);
    summaries.set(version, summary);
  }

  return summaries;
}
